<template>
    <div class="space-y-4">
        <!-- Create new token -->
        <Card>
            <CardHeader>
                <div class="flex items-center gap-2">
                    <KeyRound class="size-4 text-muted-foreground" />
                    <CardTitle class="text-sm font-medium text-primary">
                        Create API token
                    </CardTitle>
                </div>
            </CardHeader>

            <CardContent>
                <div class="space-y-5">
                    <p class="text-sm text-muted-foreground">
                        API tokens allow third-party services to authenticate with our application on your behalf.
                    </p>

                    <div class="w-full max-w-[373px]">
                        <Label for="token-name" class="text-sm font-medium text-foreground">Name</Label>
                        <Input
                            id="token-name"
                            v-model="form.name"
                            class="mt-2"
                            placeholder="Type in name"
                        />
                        <p v-if="errors.name" class="mt-1 text-xs text-new-red-500">{{ errors.name }}</p>
                    </div>

                    <div>
                        <Label class="text-sm font-medium text-foreground">Permissions</Label>
                        <div class="mt-3 grid grid-cols-2 gap-3 max-w-[373px]">
                            <label class="flex items-center gap-2 cursor-pointer">
                                <Checkbox v-model="form.read" />
                                <span class="text-sm text-foreground">Read</span>
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer">
                                <Checkbox v-model="form.create" />
                                <span class="text-sm text-foreground">Create</span>
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer">
                                <Checkbox v-model="form.update" />
                                <span class="text-sm text-foreground">Update</span>
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer">
                                <Checkbox v-model="form.delete" />
                                <span class="text-sm text-foreground">Delete</span>
                            </label>
                        </div>
                    </div>

                    <div class="flex">
                        <Button
                            :disabled="loading || !form.name.trim() || (!isAdmin && !isOwner)"
                            @click="createToken"
                        >
                            Create token
                        </Button>
                    </div>

                    <p v-if="!isAdmin && !isOwner" class="text-xs text-muted-foreground">
                        Only the team owner and admin can create API tokens.
                    </p>
                </div>
            </CardContent>
        </Card>

        <!-- Manage existing tokens -->
        <Card v-if="tokens.length">
            <CardHeader>
                <div class="flex items-center gap-2">
                    <ListChecks class="size-4 text-muted-foreground" />
                    <CardTitle class="text-sm font-medium text-primary">
                        Manage API tokens
                    </CardTitle>
                </div>
            </CardHeader>

            <CardContent>
                <p class="text-sm text-muted-foreground">
                    You may delete any of your existing tokens if they are no longer needed.
                </p>

                <div class="mt-4 space-y-2">
                    <div
                        v-for="token in tokens"
                        :key="token.id"
                        class="flex items-center justify-between bg-new-neutral-50 px-3 py-2.5"
                    >
                        <span class="text-sm text-foreground">{{ token.name }}</span>
                        <div class="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                :disabled="loading"
                                @click="editingToken = token"
                            >
                                Permissions
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                class="text-destructive"
                                :disabled="loading"
                                @click="confirmDeleteToken(token)"
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>

        <!-- Show newly-created token's plaintext value once -->
        <ApiTokenSecretDialogV2
            v-if="newToken"
            :token="newToken"
            @close="newToken = ''"
        />

        <!-- Edit permissions on existing token -->
        <UpdateApiTokenPermissionsDialogV2
            v-if="editingToken"
            :api-token="editingToken"
            @close="editingToken = null"
        />

        <!-- Delete confirmation -->
        <Dialog :open="!!pendingDelete" @update:open="(val) => !val && (pendingDelete = null)">
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete API token</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete <span class="font-medium text-foreground">{{ pendingDelete?.name }}</span>?
                        Any service using this token will lose access immediately.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter class="mt-4">
                    <Button variant="outline" @click="pendingDelete = null">Cancel</Button>
                    <Button variant="destructive" @click="deleteToken">Delete</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue';
import axios, { AxiosError } from 'axios';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { KeyRound, ListChecks } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import usePermissions from '@/composables/usePermissions';
import ApiTokenSecretDialogV2 from './ApiTokenSecretDialogV2.vue';
import UpdateApiTokenPermissionsDialogV2 from './UpdateApiTokenPermissionsDialogV2.vue';

interface IApiToken {
    id: number;
    name: string;
    abilities: string[];
}

defineProps({
    tokens: {
        type: Array as PropType<IApiToken[]>,
        required: true,
    },
});

const { isAdmin, isOwner } = usePermissions();

const loading = ref(false);
const newToken = ref('');
const editingToken = ref<IApiToken | null>(null);
const pendingDelete = ref<IApiToken | null>(null);
const errors = ref<Record<string, string>>({});

const form = ref({
    name: '',
    create: false,
    read: false,
    update: false,
    delete: false,
});

const resetForm = () => {
    form.value = { name: '', create: false, read: false, update: false, delete: false };
    errors.value = {};
};

const createToken = async () => {
    if (!form.value.name.trim()) {
        errors.value = { name: 'Name is required.' };
        return;
    }
    loading.value = true;
    errors.value = {};

    try {
        const res = await axios.post(route('settings.api-tokens.store'), form.value);
        newToken.value = res.data?.data?.plainTextToken ?? '';
        resetForm();
        router.reload({ only: ['tokens'], preserveScroll: true, preserveState: true });
        toast.success('API token created');
    } catch (err) {
        if (err instanceof AxiosError) {
            const fieldErrors = err.response?.data?.errors ?? {};
            errors.value = Object.fromEntries(
                Object.entries(fieldErrors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : String(v)])
            );
            toast.error(err.response?.data?.message ?? 'Failed to create API token');
        } else {
            toast.error('Failed to create API token');
        }
    } finally {
        loading.value = false;
    }
};

const confirmDeleteToken = (token: IApiToken) => {
    pendingDelete.value = token;
};

const deleteToken = () => {
    const token = pendingDelete.value;
    if (!token) return;
    pendingDelete.value = null;

    loading.value = true;
    router.delete(route('settings.api-tokens.destroy', token.id), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            toast.success('API token deleted');
            router.reload({ only: ['tokens'], preserveScroll: true, preserveState: true });
        },
        onError: (errs) => {
            const message = (Object.values(errs)[0] as string) || 'Failed to delete API token';
            toast.error(message);
        },
        onFinish: () => {
            loading.value = false;
        },
    });
};
</script>
